import {FormControl} from '@angular/forms';
import type {CmsApiService} from '../../core/cms-api.service';
import {
  ImportModuleAdminEventGroup,
  ImportModuleBlankValues,
  ImportModuleConceptCandidate,
  ImportModuleConceptField,
  ImportModuleConceptRow,
  ImportModuleDecisionRow,
  ImportModuleGroupFlags,
  ImportModuleGroupIdKind,
  ImportModuleGroupListKind,
  ImportModuleGroupPayloads,
  ImportModuleHistoryEventGroup,
  ImportModuleHistoryEventNamedParty,
  ImportModuleLookup,
  ImportModuleMappingEntry,
  ImportModuleMediaJoinSeveral,
  ImportModuleMediaMappingEntry,
  ImportModuleMediaMatch,
  ImportModuleMediaSetting,
  ImportModuleRunSpec,
  ImportModuleStoredDecision,
} from '../../core/definitions/import-module';
import {
  GroupCardDescriptor,
  GroupCardModel,
  GroupKind,
  GroupMemberRow,
  GroupSubBlockRow,
  NAMED_OBJECT_TYPE_LABEL_KEY,
  namedTakesNames,
  namedTypeMissing,
} from './group-card/group-card.model';
import {completeLookupKeys, LookupDefinition, lookupPayload} from './lookup-controls';
import {TemplatePart} from './value-template';
import {ValueSourceControls, valueSourceParts} from './value-source';
import type {ImportView} from './step-bar/import-steps';

/**
 * The Import module page's request bodies, built from the page's state:
 * the mapping list, the group lists built from it, and one builder per
 * body. Each builder builds the mapping list at most once.
 */

/**
 * Sentinel `target_field` for a column that already holds the object's
 * Primus identification number, imported as-is. The identification number
 * is not among the fields `GET /target_fields` returns.
 */
export const IDENTIFICATION_NUMBER_TARGET = '__identification_number__';

/**
 * Synthetic targets for the object's registration date and registrar: the
 * created revision the object is saved under, read from the source instead
 * of taken from the run. Neither is among the fields `GET /target_fields`
 * returns. Value templates like any other, so a registrar code can go
 * through a lookup.
 */
export const CREATED_AT_TARGET = '__created_at__';
export const CREATED_BY_TARGET = '__created_by__';

/** Every synthetic target: never sent where the backend expects field ids. */
const PSEUDO_TARGETS: ReadonlySet<string> =
  new Set([IDENTIFICATION_NUMBER_TARGET, CREATED_AT_TARGET, CREATED_BY_TARGET]);

/**
 * The group-card kinds that share the `group_id` wire key and a
 * `{key, ...headerValues}` group shape, so their stored groups need no
 * per-kind field beyond which kind they are. History events and admin
 * events have wire keys and shapes of their own.
 */
export type GroupIdKind = ImportModuleGroupIdKind;
// Fails to compile while a `group_id` kind is missing here.
const GROUP_ID_KIND_SET: Record<GroupIdKind, true> =
  {reference: true, alternative_no: true, measurement: true, material: true, title: true};
export const GROUP_ID_KINDS = Object.keys(GROUP_ID_KIND_SET) as readonly GroupIdKind[];

/** Every kind with a group list of its own, in the order a profile's lists
 * are read: the history events, the `group_id` kinds, the admin events. */
export const GROUP_LIST_KINDS: readonly ImportModuleGroupListKind[] =
  ['history_event', ...GROUP_ID_KINDS, 'admin_event'];

/** The wire key of a kind's group list. */
export type GroupListKey<K extends ImportModuleGroupListKind = ImportModuleGroupListKind> = `${K}_groups`;
export const groupListKey = <K extends ImportModuleGroupListKind>(kind: K): GroupListKey<K> => `${kind}_groups`;

/** The wire key of a `group_id` kind's group list. */
export type GroupIdWireKey = GroupListKey<GroupIdKind>;
export const groupIdWireKey = groupListKey<GroupIdKind>;

/** The `/batch_fields` flag naming that a kind has a card, `undefined` for
 * a kind with none (a placement card is an admin-event card there). */
export function groupFlagKey(kind: GroupKind): keyof ImportModuleGroupFlags | undefined {
  return kind === 'placement_event' ? undefined : `has_${kind}_group`;
}

/** The `admin_event_type` of an admin-event card's group (backend
 * `constants.OBJECT_TYPE_GENERIC_EVENT`). */
const ADMIN_EVENT_GENERIC_KIND = 'GenericEvent';
/** The `admin_event_type` of a Fast plassering card's group (backend
 * `constants.OBJECT_TYPE_PLACEMENT_EVENT`). */
export const PLACEMENT_EVENT_KIND = 'PlacementEvent';

/** One `mapping` entry: a flat assignment or a group-card member row. */
export type MappingEntry = ImportModuleMappingEntry;

/** The full in-progress configuration stashed to sessionStorage so it
 * survives a reload: the wire shape a saved profile uses (see
 * `ImportModuleProfile`), plus the table it belongs to and the step view
 * shown. */
export interface SessionSnapshot {
  objectType: string;
  selectedTable: string;
  mapping: ImportModuleMappingEntry[];
  historyEventGroups: ImportModuleHistoryEventGroup[];
  groupIdGroups: Partial<Record<GroupIdKind, {key: string}[]>>;
  adminEventGroups: ImportModuleAdminEventGroup[];
  batch: { [key: string]: string };
  media: MediaSetting | null;
  collectionId: string | null;
  collectionLabel: string | null;
  regLevelId: string | null;
  regLevelLabel: string | null;
  statusId: string | null;
  statusLabel: string | null;
  publish: boolean;
  overwriteExisting: boolean;
  // Absent in a snapshot stashed before blank values existed.
  blankValues?: ImportModuleBlankValues;
  // Absent in a snapshot stashed before rejected values were kept.
  blankValuesRejected?: ImportModuleBlankValues;
  // Absent in a snapshot stashed before lookups were set up on their own.
  lookups?: ImportModuleLookup[];
  // The last value-review scan, with the operator's decisions on its rows;
  // absent when no scan has run.
  valueReview?: { fields: ImportModuleConceptField[]; rowsScanned: number };
  // The loaded profile: a scan pre-fills its decisions, and the review
  // saves to it.
  profileId?: string | null;
  // The loaded profile's decisions, which a save sends while no scan
  // holds rows of its own; absent when none are held.
  profileDecisions?: ImportModuleDecisionRow[];
  // The decisions of a profile imported from a file and neither saved nor
  // replaced since, which a scan is sent in place of a profile; absent
  // when there is none.
  carriedDecisions?: ImportModuleStoredDecision[];
  // The step view shown; absent in a snapshot stashed before the page had
  // steps, which resumes on the first step.
  view?: ImportView;
  // The profile as last applied or saved (see `profileComparison`), `null`
  // when there is none; absent in a snapshot stashed before it was kept.
  profileBaseline?: ProfileComparison | null;
}

// --- the page state the builders read ---------------------------------------

/** A flat mapping row, as far as the mapping reads it. */
export interface FlatMappingRow extends ValueSourceControls {
  targetField: {field_id: string} | null;
  conceptRootControl: FormControl<string | null>;
}

/** Every group card on the page by kind, with each kind's descriptor.
 * `kinds` is the order the kinds' mapping entries go out in. */
export interface GroupCards {
  kinds: readonly GroupKind[];
  models: Readonly<Record<GroupKind, readonly GroupCardModel[]>>;
  descriptors: Readonly<Record<GroupKind, GroupCardDescriptor>>;
}

/** What the mapping list is built from. `columnNames` decides which
 * `{Column}` placeholders are columns. `mediaAssignments` are the rows
 * onto a field of the linked media, which the mapping list never holds
 * (see `buildMediaMapping`). */
export interface MappingState {
  assignments: readonly FlatMappingRow[];
  mediaAssignments: readonly FlatMappingRow[];
  groups: GroupCards;
  columnNames: ReadonlySet<string>;
  lookupDefinitions: readonly LookupDefinition[];
  // Set, the mapping list leaves out each member row's entry whose card
  // lacks a pick for it (`lackingPicks`). Only `livePreviewState` sets it.
  holdsBack?: boolean;
}

/** The uploaded source and the table read from it. */
export interface SourceFile {
  dmsIdentifier: string;
  fileName: string;
  table: string;
}

/** What the run does with a row whose media file matches none. */
export type MediaOnMissing = NonNullable<ImportModuleRunSpec['media_join_on_missing']>;

/** The match rule a media join uses unless another is picked. */
export const DEFAULT_MEDIA_MATCH: ImportModuleMediaMatch = {mode: 'file_name', pattern: '', ignore_leading_zeros: false};

/** A named rule the pattern match offers. */
export type MediaMatchPresetId = 'before_underscore' | 'before_space' | 'code_two_numbers' | 'custom';

/** A named rule: `pattern` is what the match rule sends as its pattern,
 * `null` for `custom`, whose pattern the operator types. */
export interface MediaMatchPreset {
  id: MediaMatchPresetId;
  labelKey: string;
  pattern: string | null;
}

/** The named rules in the order they are offered. Each pattern means the
 * same as a JavaScript and a Python regular expression, matched without
 * regard to case, and keys its group 1. The two cut rules end the key at
 * their separator, or at a trailing extension that starts with a letter, so
 * a dotted number such as `NMK.1985.0123` keys as itself, with or without
 * its `.tif`. */
export const MEDIA_MATCH_PRESETS: readonly MediaMatchPreset[] = [
  {id: 'before_underscore', labelKey: 'TRANS__IMPORT__MEDIA_MATCH_BEFORE_UNDERSCORE', pattern: '^([^_]+?)(?:_|\\.[a-z][a-z0-9]{1,4}$|$)'},
  {id: 'before_space', labelKey: 'TRANS__IMPORT__MEDIA_MATCH_BEFORE_SPACE', pattern: '^(\\S+?)(?:\\s|\\.[a-z][a-z0-9]{1,4}$|$)'},
  {id: 'code_two_numbers', labelKey: 'TRANS__IMPORT__MEDIA_MATCH_CODE_TWO_NUMBERS', pattern: '^([a-z]+\\d+-\\d+)'},
  {id: 'custom', labelKey: 'TRANS__IMPORT__MEDIA_MATCH_CUSTOM', pattern: null},
];

/** The rule the pattern match starts from. */
export const DEFAULT_MEDIA_MATCH_PRESET: MediaMatchPresetId = 'before_underscore';

/** The rule a saved `pattern` shows: the preset with that pattern, else
 * `custom` for any other non-blank pattern, else the default. */
export function mediaMatchPresetFor(pattern: string): MediaMatchPresetId {
  if (!pattern.trim()) {
    return DEFAULT_MEDIA_MATCH_PRESET;
  }
  return MEDIA_MATCH_PRESETS.find(preset => preset.pattern === pattern)?.id ?? 'custom';
}

/** The pattern the preset `id` sends; `null` for `custom`. */
export function mediaMatchPresetPattern(id: MediaMatchPresetId): string | null {
  return MEDIA_MATCH_PRESETS.find(preset => preset.id === id)?.pattern ?? null;
}

/** The media join as picked: `joinColumn` and `folderId` are unset while
 * media linking is off, and `folderId` also while no folder is picked. */
export interface MediaJoin {
  joinColumn: string | undefined;
  folderId: string | undefined;
  onMissing: MediaOnMissing;
  match: ImportModuleMediaMatch;
  several: ImportModuleMediaJoinSeveral;
}

/** The media join as a profile and the session stash keep it. */
export type MediaSetting = ImportModuleMediaSetting;

/** The batch defaults' collection, registration level and object status,
 * each an id and its label, as the batch-defaults view holds them - any of
 * them may be unset. */
export interface BatchDefaultValues {
  collectionId: string | null | undefined;
  collectionLabel: string | null | undefined;
  regLevelId: string | null | undefined;
  regLevelLabel: string | null | undefined;
  statusId: string | null | undefined;
  statusLabel: string | null | undefined;
}

/** A stretch of the file to run (1-based, inclusive, either end open). */
export interface RowRange {
  first: number | null;
  last: number | null;
}

/** The page state every body that carries the configuration reads. */
export interface ConfigurationState extends MappingState {
  source: SourceFile;
  objectType: string;
  batch: { [key: string]: string };
  blankValues: ImportModuleBlankValues;
  publish: boolean;
  media: MediaJoin;
}

export interface PreflightState extends ConfigurationState {
  // Unset, the body carries no value decisions.
  valueDecisions?: ImportModuleConceptRow[];
  sampleSize: number;
  // `null` while the batch-defaults view does not exist.
  batchDefaults: BatchDefaultValues | null;
  rowRange: RowRange;
  // Asks for the media overview of the whole table.
  mediaOverview?: boolean;
}

export interface RunSpecState extends ConfigurationState {
  valueDecisions: ImportModuleConceptRow[];
  // A collection and a registration level are required for a run.
  batchDefaults: BatchDefaultValues & {collectionId: string; regLevelId: string};
  overwriteExisting: boolean;
  rowRange: RowRange;
}

export interface ProfileSaveState extends ConfigurationState {
  profileName: string;
  // The media section a save keeps while no join column is picked (see
  // `mediaSetting`); unset for none.
  keptMedia?: MediaSetting | null;
  blankValuesRejected: ImportModuleBlankValues;
  valueDecisions: ImportModuleDecisionRow[];
  batchDefaults: BatchDefaultValues;
  overwriteExisting: boolean;
}

export interface SessionSnapshotState extends ConfigurationState {
  // As on `ProfileSaveState`.
  keptMedia?: MediaSetting | null;
  blankValuesRejected: ImportModuleBlankValues;
  batchDefaults: BatchDefaultValues;
  overwriteExisting: boolean;
  valueReview: SessionSnapshot['valueReview'];
  profileId: string | null;
  profileDecisions: ImportModuleDecisionRow[];
  carriedDecisions: ImportModuleStoredDecision[] | null;
  view: ImportView;
  profileBaseline: ProfileComparison | null;
}

export interface ConceptScanState extends MappingState {
  source: SourceFile;
  // Unset before an object type is picked.
  objectType: string | undefined;
  blankValues: ImportModuleBlankValues;
}

export interface BatchFieldsState extends MappingState {
  objectType: string;
  media: MediaJoin;
}

// --- the request bodies -------------------------------------------------------

export type PreflightRequest = Parameters<CmsApiService['importModulePreflight']>[0];
/** What `/concept_values` is asked, less the loaded profile and the
 * decisions sent in its place. */
export type ConceptScanRequest =
  Omit<Parameters<CmsApiService['importModuleConceptValues']>[0], 'profile_id' | 'value_decisions'>;
export type ProfileSaveRequest = Parameters<CmsApiService['importModuleSaveProfile']>[0];
export type BatchFieldsRequest = Parameters<CmsApiService['importModuleBatchFields']>[0];
export type LookupCheckRequest = Parameters<CmsApiService['importModuleLookupCheck']>[0];

// --- the mapping ----------------------------------------------------------------

/**
 * The mapping list: one entry per flat row with a target and a value, then
 * one per group-card member row that resolves to a value, kind by kind in
 * `groups.kinds` order. A group member's entry carries its card's key in
 * `history_event_group`, `admin_event_group` or `group_id`, and a Named or
 * Place sub-block member's also its sub-block's key in `sub_block`.
 *
 * With `targetField`, only that field's entries, and only rows mapped onto
 * it are read.
 */
export function buildMapping(state: MappingState, targetField?: string): MappingEntry[] {
  return Array.from(sentRows(state, targetField), sent => sent.entry);
}

/** Whether the mapping list has an entry. Reads rows only until it finds
 * one. */
export function hasMappingEntry(state: MappingState): boolean {
  return !sentRows(state).next().done;
}

/** What reading one row into a mapping entry needs. */
interface EntryContext {
  columnNames: ReadonlySet<string>;
  lookupKeys: ReadonlySet<string>;
  // Whether a row mapped onto this field is read at all.
  wanted: (fieldId: string) => boolean;
}

function entryContext(state: MappingState, targetField?: string): EntryContext {
  return {
    columnNames: state.columnNames,
    lookupKeys: completeLookupKeys(state.lookupDefinitions),
    wanted: fieldId => targetField === undefined || fieldId === targetField,
  };
}

/** A member row that sends a mapping entry: the card it is on, the Named
 * or Place sub-block it is the member of, if it is one's, and its entry. */
interface SentMember {
  kind: 'member';
  row: GroupMemberRow;
  card: GroupCardModel;
  sub?: GroupSubBlockRow;
  entry: MappingEntry;
}

/** A flat row that sends a mapping entry, and its entry. */
interface SentFlat {
  kind: 'flat';
  row: FlatMappingRow;
  entry: MappingEntry;
}

/** A row that sends a mapping entry: a flat row or a member row. */
type SentRow = SentFlat | SentMember;

/** The rows that send an entry, in the order the mapping list holds
 * their entries; with `state.holdsBack`, without the member rows whose
 * card lacks a pick for them (`lackingPicks`). */
function* sentRows(state: MappingState, targetField?: string): Generator<SentRow> {
  const context = entryContext(state, targetField);
  yield* flatEntries(state.assignments, context);
  for (const [sent, descriptor] of sentMembers(state, context)) {
    if (!state.holdsBack || lackingPicks(sent, descriptor) === null) {
      yield sent;
    }
  }
}

/** Every member row that sends an entry, with its card's descriptor, kind
 * by kind in `groups.kinds` order. */
function* sentMembers(state: MappingState, context: EntryContext): Generator<[SentMember, GroupCardDescriptor]> {
  for (const kind of state.groups.kinds) {
    const descriptor = state.groups.descriptors[kind];
    for (const model of state.groups.models[kind]) {
      for (const sent of cardEntries(model, descriptor, context)) {
        yield [sent, descriptor];
      }
    }
  }
}

function* flatEntries(assignments: readonly FlatMappingRow[], context: EntryContext): Generator<SentFlat> {
  for (const assignment of assignments) {
    if (!assignment.targetField || !context.wanted(assignment.targetField.field_id)) {
      continue;
    }
    const parts = valueSourceParts(assignment, context.columnNames, context.lookupKeys);
    if (parts.length) {
      const entry: MappingEntry = {
        target_field: assignment.targetField.field_id,
        parts,
        ...(assignment.conceptRootControl.value ? {concept_root_id: assignment.conceptRootControl.value} : {}),
      };
      yield {kind: 'flat', row: assignment, entry};
    }
  }
}

/** One card's entries: its member rows, then its Named and Place
 * sub-blocks' members. */
function* cardEntries(
  model: GroupCardModel, descriptor: GroupCardDescriptor, context: EntryContext,
): Generator<SentMember> {
  for (const row of model.members.filter(member => context.wanted(member.fieldId))) {
    const entry = groupMemberEntry(row, isConceptMember(descriptor, row), context);
    if (entry) {
      yield {kind: 'member', row, card: model, entry: {...entry, ...cardKeyOf(descriptor.kind, model.key)}};
    }
  }
  const subBlocks = model.subBlocks.filter(
    sub => (sub.defKind === 'named' || sub.defKind === 'place') && context.wanted(sub.member.fieldId));
  for (const sub of subBlocks) {
    const entry = groupMemberEntry(sub.member, true, context);
    if (entry) {
      yield {
        kind: 'member', row: sub.member, card: model, sub,
        entry: {...entry, history_event_group: model.key, sub_block: sub.key},
      };
    }
  }
}

/** Whether `row` is a member of a concept field on `descriptor`'s card. */
export function isConceptMember(descriptor: GroupCardDescriptor, row: GroupMemberRow): boolean {
  return !!descriptor.members.find(m => m.fieldId === row.fieldId)?.concept;
}

/** The flat rows and group-card member rows that send a mapping entry. */
export interface EntryRows {
  flat: ReadonlySet<FlatMappingRow>;
  // Each card with a member row that sends an entry, and those rows in the
  // order their entries go out.
  cards: ReadonlyMap<GroupCardModel, readonly GroupMemberRow[]>;
  // The rows among them whose entry takes a value through a lookup.
  lookupReaders: ReadonlySet<FlatMappingRow | GroupMemberRow>;
}

/** The rows `buildMapping` reads an entry from. */
export function entryRows(state: MappingState): EntryRows {
  const flat = new Set<FlatMappingRow>();
  const cards = new Map<GroupCardModel, GroupMemberRow[]>();
  const lookupReaders = new Set<FlatMappingRow | GroupMemberRow>();
  for (const sent of sentRows(state)) {
    if (sent.kind === 'flat') {
      flat.add(sent.row);
    } else {
      const rows = cards.get(sent.card);
      if (rows) {
        rows.push(sent.row);
      } else {
        cards.set(sent.card, [sent.row]);
      }
    }
    if (sent.entry.parts.some(part => part.kind === 'lookup')) {
      lookupReaders.add(sent.row);
    }
  }
  return {flat, cards, lookupReaders};
}

/** The rows onto a field of the linked media that `buildMediaMapping`
 * reads an entry from, whatever the join column, and those among them
 * whose entry takes a value through a lookup. */
export function mediaEntryRows(state: MappingState): Pick<EntryRows, 'flat' | 'lookupReaders'> {
  const flat = new Set<FlatMappingRow>();
  const lookupReaders = new Set<FlatMappingRow>();
  for (const {row, entry} of flatEntries(state.mediaAssignments, entryContext(state))) {
    flat.add(row);
    if (entry.parts.some(part => part.kind === 'lookup')) {
      lookupReaders.add(row);
    }
  }
  return {flat, lookupReaders};
}

/** The wire key that routes a `kind` card's member entries to the card. */
function cardKeyOf(kind: GroupKind, key: string): Partial<MappingEntry> {
  switch (kind) {
    case 'history_event':
      return {history_event_group: key};
    case 'admin_event':
    case 'placement_event':
      return {admin_event_group: key};
    default:
      return {group_id: key};
  }
}

/** A group / sub-block member row as a `mapping` entry, or `null` when it
 * resolves to nothing. On a concept member: `value_is_kulturnav_uuid` when
 * its value is a KulturNav objectid, else `value_is_resolved_id` for a
 * picked fixed value. */
function groupMemberEntry(row: GroupMemberRow, concept: boolean, context: MemberContext): MappingEntry | null {
  const parts = memberParts(row, concept, context);
  if (!parts.length) {
    return null;
  }
  const entry: MappingEntry = {target_field: row.fieldId, parts, member_source: row.sourceControl.value};
  if (concept && !memberFixedIsPick(row, concept)) {
    entry.value_is_kulturnav_uuid = true;
  } else if (row.sourceControl.value === 'constant' && concept) {
    entry.value_is_resolved_id = true;
  }
  return entry;
}

/** What reading a member row's value needs: which `{Column}` placeholders
 * are columns, and the keys of the complete lookups. */
type MemberContext = Pick<EntryContext, 'columnNames' | 'lookupKeys'>;

/** Whether a member row's fixed value is a picked record
 * (`conceptControl`) rather than typed text (`constantControl`): on a
 * concept member whose value is a name. A KulturNav objectid is pasted
 * text, on a concept member too. */
export function memberFixedIsPick(row: GroupMemberRow, concept: boolean): boolean {
  return concept && row.valueTypeControl.value !== 'kulturnav_uuid';
}

/** Whether a group or sub-block member row sends a mapping entry.
 * `concept`: the row is a concept member (a Named or Place sub-block's
 * member always is). */
export function memberRowSends(
  row: GroupMemberRow, concept: boolean, columnNames: ReadonlySet<string>, lookupKeys: ReadonlySet<string>,
): boolean {
  return memberParts(row, concept, {columnNames, lookupKeys}).length > 0;
}

/** A member row's value as `parts`: its value source's parts for the
 * "Fra kolonne" source (see `valueSourceParts`), or a single fixed-value
 * `text` part (the picked id where `memberFixedIsPick`, the trimmed typed
 * text otherwise). Empty when nothing is set. */
function memberParts(row: GroupMemberRow, concept: boolean, context: MemberContext): TemplatePart[] {
  if (row.sourceControl.value === 'column') {
    return valueSourceParts(row, context.columnNames, context.lookupKeys);
  }
  const value = (memberFixedIsPick(row, concept) ? row.conceptControl.value : row.constantControl.value.trim()) || '';
  return value ? [{kind: 'text', value}] : [];
}

/**
 * The `media_mapping` list: one entry per row onto a field of the linked
 * media with a target and a value, each with its field and parts alone.
 * Empty without a join column, which a field on linked media needs.
 */
export function buildMediaMapping(state: MappingState, media: MediaJoin): ImportModuleMediaMappingEntry[] {
  if (!media.joinColumn) {
    return [];
  }
  return Array.from(flatEntries(state.mediaAssignments, entryContext(state)), ({entry}) => ({
    target_field: entry.target_field, parts: entry.parts,
  }));
}

/** `mapping` without the entries for a synthetic target. */
function withoutPseudoTargets(mapping: readonly MappingEntry[]): MappingEntry[] {
  return mapping.filter(entry => !PSEUDO_TARGETS.has(entry.target_field));
}

/** The keys of the groups `mapping` routes a value to by `group_id`. */
export function usedGroupIds(mapping: readonly MappingEntry[]): Set<string> {
  return new Set(mapping.map(entry => entry.group_id).filter((key): key is string => !!key));
}

/** The keys of the sub-blocks `mapping` routes a value to. */
export function usedSubBlockKeys(mapping: readonly MappingEntry[]): Set<string> {
  return new Set(mapping.map(entry => entry.sub_block).filter((key): key is string => !!key));
}

// --- the group lists ------------------------------------------------------------

/** A Named sub-block's `object_type`, and its `type_id` while it takes
 * names from a column (`namedTakesNames`): a KulturNav uuid brings its
 * record's own type, and a fixed pick creates nothing. Empty for a Place
 * sub-block. */
function namedTargetPayload(
  sub: GroupSubBlockRow,
): Pick<ImportModuleHistoryEventNamedParty, 'object_type' | 'type_id'> {
  if (!sub.target) {
    return {};
  }
  return {
    object_type: sub.target.objectTypeControl.value,
    type_id: namedTakesNames(sub) ? sub.target.typeIdControl.value ?? undefined : undefined,
  };
}

/** Whether a history-event card is sent as a group: its event type is
 * picked. */
function historyEventGroupSent(model: GroupCardModel): boolean {
  return !!model.headerControls['event_type_id']?.value;
}

/**
 * `history_event_groups`: the groups with an event type picked. Each
 * group's `named` / `places` lists carry only the sub-blocks in
 * `usedSubBlocks` - those a mapping entry routes a value to.
 */
export function historyEventGroupsPayload(
  models: readonly GroupCardModel[], usedSubBlocks: ReadonlySet<string>,
): ImportModuleHistoryEventGroup[] {
  const subBlocksOf = (model: GroupCardModel, kind: 'named' | 'place') => model.subBlocks
    .filter(sub => sub.defKind === kind && usedSubBlocks.has(sub.key))
    .map(sub => ({
      key: sub.key,
      role_id: sub.headerControls['role_id']?.value ?? undefined,
      role_status_id: sub.headerControls['role_status_id']?.value ?? undefined,
      ...namedTargetPayload(sub),
    }));
  return models
    .filter(historyEventGroupSent)
    .map(model => ({
      key: model.key,
      event_type_id: model.headerControls['event_type_id'].value!,
      named: subBlocksOf(model, 'named'),
      places: subBlocksOf(model, 'place'),
    }));
}

/**
 * The groups of one `GroupIdKind`: those in `usedIds` (the backend rejects
 * a group no mapping entry routes to) with every required header field of
 * `descriptor` picked. Each carries every header field, `undefined` when
 * unpicked, and the create-missing option when it is on.
 */
export function groupIdKindPayload<T extends {key: string}>(
  models: readonly GroupCardModel[], descriptor: GroupCardDescriptor, usedIds: ReadonlySet<string>,
): T[] {
  const requiredKeys = descriptor.header.filter(h => h.required).map(h => h.key);
  return models
    .filter(model => usedIds.has(model.key) && requiredKeys.every(key => !!model.headerControls[key]?.value))
    .map(model => {
      const group = {key: model.key} as T;
      for (const h of descriptor.header) {
        (group as any)[h.key] = model.headerControls[h.key]?.value || undefined;
      }
      if (model.createMissingControl?.value) {
        (group as any).create_missing = true;
        (group as any).create_missing_parent_id = model.createMissingParentControl?.value || undefined;
      }
      return group;
    });
}

/** Whether an admin-event card is sent as a group: its sub type and its
 * purpose type are picked. */
function adminEventGroupSent(model: GroupCardModel): boolean {
  return !!model.headerControls['admin_event_sub_type_id']?.value
    && !!model.headerControls['admin_event_purpose_type_id']?.value;
}

/**
 * `admin_event_groups`: the admin-event cards with both a sub type and a
 * purpose type picked, whether or not a member is mapped (a header-only
 * group is a complete configuration), then every Fast plassering card.
 */
export function adminEventGroupsPayload(
  genericModels: readonly GroupCardModel[], placementModels: readonly GroupCardModel[],
): ImportModuleAdminEventGroup[] {
  const generic = genericModels
    .filter(adminEventGroupSent)
    .map(model => ({
      key: model.key,
      admin_event_type: ADMIN_EVENT_GENERIC_KIND,
      sub_type_id: model.headerControls['admin_event_sub_type_id'].value!,
      purpose_type_id: model.headerControls['admin_event_purpose_type_id'].value!,
      only_when_mapped: model.onlyWhenMappedControl?.value || undefined,
    }));
  const placement = placementModels.map(model => ({key: model.key, admin_event_type: PLACEMENT_EVENT_KIND}));
  return [...generic, ...placement];
}

/** Every kind's group list, empty lists included. */
interface GroupLists {
  historyEvent: ImportModuleHistoryEventGroup[];
  groupId: Record<GroupIdKind, {key: string}[]>;
  adminEvent: ImportModuleAdminEventGroup[];
}

function groupLists(groups: GroupCards, mapping: readonly MappingEntry[]): GroupLists {
  const ids = usedGroupIds(mapping);
  return {
    historyEvent: historyEventGroupsPayload(groups.models.history_event, usedSubBlockKeys(mapping)),
    groupId: Object.fromEntries(GROUP_ID_KINDS.map(
      kind => [kind, groupIdKindPayload(groups.models[kind], groups.descriptors[kind], ids)],
    )) as Record<GroupIdKind, {key: string}[]>,
    adminEvent: adminEventGroupsPayload(groups.models.admin_event, groups.models.placement_event),
  };
}

function nonEmpty<T>(list: T[]): T[] | undefined {
  return list.length ? list : undefined;
}

/** Each `group_id` kind's group list from `list`, under its wire key. */
function groupIdPayloads(
  list: (kind: GroupIdKind) => {key: string}[] | undefined,
): Pick<ImportModuleGroupPayloads, GroupIdWireKey> {
  return Object.fromEntries(GROUP_ID_KINDS.map(kind => [groupIdWireKey(kind), list(kind)])) as
    Pick<ImportModuleGroupPayloads, GroupIdWireKey>;
}

/** Every kind's group list from one mapping list (see `ImportModuleGroupPayloads`). */
export function groupPayloads(groups: GroupCards, mapping: readonly MappingEntry[]): ImportModuleGroupPayloads {
  const lists = groupLists(groups, mapping);
  return {
    history_event_groups: nonEmpty(lists.historyEvent),
    ...groupIdPayloads(kind => nonEmpty(lists.groupId[kind])),
    admin_event_groups: nonEmpty(lists.adminEvent),
  };
}

// --- what the live preview holds back ----------------------------------------------

/** Whether a Named or Place sub-block has the picks its entry needs: a
 * role, and on a NamedObject that takes names its type. */
export function subBlockPicked(sub: GroupSubBlockRow): boolean {
  return !!sub.headerControls['role_id']?.value && !namedTypeMissing(sub);
}

/** Whether `/preflight` knows the group an entry of `card` names: a
 * history-event or admin-event card sent as a group. It takes the entry
 * of any other kind's card without one. */
function entryGroupSent(kind: GroupKind, card: GroupCardModel): boolean {
  switch (kind) {
    case 'history_event':
      return historyEventGroupSent(card);
    case 'admin_event':
      return adminEventGroupSent(card);
    default:
      return true;
  }
}

/** A header pick's title key: its `label` when that is a `TRANS__` key,
 * else ''. A header whose option list has not come yet is labelled by
 * something else, which is no title to show. */
function titleKey(label: string | undefined): string {
  return label?.startsWith('TRANS__') ? label : '';
}

/**
 * The picks the card of `sent` lacks for `/preflight` to take the entry,
 * each by the `TRANS__` key of its title, or '' when its label is not one
 * (`titleKey`): the required header picks of a card whose group is not
 * sent (`entryGroupSent`), and for a Named or Place sub-block's entry the
 * role and the named-object type it lacks (`subBlockPicked`). `null`
 * when it lacks none. `/preflight` answers an entry sent without them
 * with a group error.
 */
function lackingPicks(sent: SentMember, descriptor: GroupCardDescriptor): string[] | null {
  const groupSent = entryGroupSent(descriptor.kind, sent.card);
  const sub = sent.sub && !subBlockPicked(sent.sub) ? sent.sub : null;
  if (groupSent && !sub) {
    return null;
  }
  const lacking: string[] = [];
  if (!groupSent) {
    lacking.push(...descriptor.header
      .filter(h => h.required && !sent.card.headerControls[h.key]?.value).map(h => titleKey(h.label)));
  }
  if (sub && !sub.headerControls['role_id']?.value) {
    const def = descriptor.subBlocks.find(d => d.kind === sub.defKind);
    lacking.push(titleKey(def?.header.find(h => h.key === 'role_id')?.label));
  }
  if (sub && namedTypeMissing(sub)) {
    lacking.push(NAMED_OBJECT_TYPE_LABEL_KEY);
  }
  return lacking;
}

/** A card the live preview leaves entries of out, and the picks it lacks
 * for them: each one's title key, once, with '' for those that have none
 * (see `lackingPicks`). */
export interface HeldBackCard {
  card: GroupCardModel;
  kind: GroupKind;
  lacking: string[];
}

/** The cards with a member row whose entry `/preflight` would not take
 * (`lackingPicks`), in the order their entries go out. */
export function heldBackCards(state: MappingState): HeldBackCard[] {
  const held = new Map<GroupCardModel, HeldBackCard>();
  for (const [sent, descriptor] of sentMembers(state, entryContext(state))) {
    const lacking = lackingPicks(sent, descriptor);
    if (lacking) {
      const card = held.get(sent.card) ?? {card: sent.card, kind: descriptor.kind, lacking: []};
      card.lacking = [...new Set([...card.lacking, ...lacking])];
      held.set(sent.card, card);
    }
  }
  return [...held.values()];
}

/**
 * `state` as the live preview reads it: its mapping list leaves out each
 * member row's entry whose card lacks a pick for it (`lackingPicks`), so
 * that no entry names a history-event or admin-event group the body does
 * not send, or a Named or Place sub-block without its role or its
 * named-object type. A card keeps its other entries. A sub-block whose
 * entry is left out is not in its group's `named` / `places` list either.
 */
export function livePreviewState<T extends MappingState>(state: T): T {
  return {...state, holdsBack: true};
}

// --- blank values, lookups, media -------------------------------------------------

/** Values read as blank or rejected as blank, without a column that has
 * none; `undefined` when no column has any. */
export function blankValuesPayload(values: ImportModuleBlankValues): ImportModuleBlankValues | undefined {
  const payload = Object.fromEntries(Object.entries(values).filter(([, held]) => held.length));
  return Object.keys(payload).length ? payload : undefined;
}

/** The complete lookups in the wire shape; `undefined` when there is none. */
export function lookupsPayload(
  lookups: readonly LookupDefinition[], columnNames: ReadonlySet<string>,
): ImportModuleLookup[] | undefined {
  const payload = lookups
    .map(lookup => lookupPayload(lookup, columnNames))
    .filter((lookup): lookup is ImportModuleLookup => lookup !== null);
  return payload.length ? payload : undefined;
}

/** The media join as a profile and the session stash keep it, with the
 * fields on the linked media while a row sends one. Without a join column
 * it is `kept`: a restored media section whose join column the source
 * lacks, else none. */
function mediaSetting(
  media: MediaJoin, mediaMapping: ImportModuleMediaMappingEntry[], kept: MediaSetting | null | undefined,
): MediaSetting | null {
  return media.joinColumn
    ? {
      join_column: media.joinColumn, on_missing: media.onMissing, match: media.match, several: media.several,
      mapping: nonEmpty(mediaMapping),
    }
    : kept ?? null;
}

/** The match rule and several-files policy a media join sends; neither
 * without a join column. */
function mediaJoinOptions(
  media: MediaJoin,
): Pick<ImportModuleRunSpec, 'media_match' | 'media_join_several'> {
  return media.joinColumn ? {media_match: media.match, media_join_several: media.several} : {};
}

function sourceParams(source: SourceFile): {dms_identifier: string; file_name: string; table: string} {
  return {dms_identifier: source.dmsIdentifier, file_name: source.fileName, table: source.table};
}

function rowRangeParams(range: RowRange): {first_row?: number; last_row?: number} {
  return {first_row: range.first || undefined, last_row: range.last || undefined};
}

// --- one builder per body ---------------------------------------------------------

/** The `/preflight` body. */
export function preflightRequest(state: PreflightState): PreflightRequest {
  const mapping = buildMapping(state);
  return {
    ...sourceParams(state.source),
    object_type: state.objectType,
    mapping,
    ...groupPayloads(state.groups, mapping),
    batch: state.batch,
    value_decisions: state.valueDecisions,
    blank_values: blankValuesPayload(state.blankValues),
    lookups: lookupsPayload(state.lookupDefinitions, state.columnNames),
    publish: state.publish,
    media_join_column: state.media.joinColumn,
    media_folder_id: state.media.folderId,
    ...mediaJoinOptions(state.media),
    media_mapping: nonEmpty(buildMediaMapping(state, state.media)),
    media_overview: state.mediaOverview || undefined,
    sample_size: state.sampleSize,
    collection_id: state.batchDefaults?.collectionId || undefined,
    ...rowRangeParams(state.rowRange),
  };
}

/** The `/run` body. */
export function runSpec(state: RunSpecState): ImportModuleRunSpec {
  const mapping = buildMapping(state);
  const defaults = state.batchDefaults;
  return {
    ...sourceParams(state.source),
    object_type: state.objectType,
    collection_id: defaults.collectionId,
    reg_level_id: defaults.regLevelId,
    status_id: defaults.statusId ?? undefined,
    publish: state.publish,
    mapping,
    ...groupPayloads(state.groups, mapping),
    batch: state.batch,
    overwrite_existing: state.overwriteExisting,
    value_decisions: state.valueDecisions,
    blank_values: blankValuesPayload(state.blankValues),
    lookups: lookupsPayload(state.lookupDefinitions, state.columnNames),
    media_join_column: state.media.joinColumn,
    media_folder_id: state.media.folderId,
    media_join_on_missing: state.media.joinColumn ? state.media.onMissing : undefined,
    ...mediaJoinOptions(state.media),
    media_mapping: nonEmpty(buildMediaMapping(state, state.media)),
    ...rowRangeParams(state.rowRange),
  };
}

/** The profile-save body. The media folder is left out: which folder holds
 * an import's media changes from one import to the next. */
export function profileSaveRequest(state: ProfileSaveState): ProfileSaveRequest {
  const mapping = buildMapping(state);
  const defaults = state.batchDefaults;
  return {
    profile_name: state.profileName,
    object_type: state.objectType,
    source_table: state.source.table || null,
    mapping,
    ...groupPayloads(state.groups, mapping),
    batch: state.batch,
    media: mediaSetting(state.media, buildMediaMapping(state, state.media), state.keptMedia),
    collection_id: defaults.collectionId,
    collection_label: defaults.collectionLabel,
    reg_level_id: defaults.regLevelId,
    reg_level_label: defaults.regLevelLabel,
    status_id: defaults.statusId,
    status_label: defaults.statusLabel,
    publish: state.publish,
    overwrite_existing: state.overwriteExisting,
    blank_values: blankValuesPayload(state.blankValues),
    blank_values_rejected: blankValuesPayload(state.blankValuesRejected),
    lookups: lookupsPayload(state.lookupDefinitions, state.columnNames),
    value_decisions: state.valueDecisions,
  };
}

/** The session stash: every group list, empty ones included, and a blank
 * value, rejected value or lookup list that is empty rather than unset. */
export function sessionSnapshot(state: SessionSnapshotState): SessionSnapshot {
  const mapping = buildMapping(state);
  const lists = groupLists(state.groups, mapping);
  const defaults = state.batchDefaults;
  return {
    objectType: state.objectType,
    selectedTable: state.source.table,
    mapping,
    historyEventGroups: lists.historyEvent,
    groupIdGroups: lists.groupId,
    adminEventGroups: lists.adminEvent,
    batch: state.batch,
    media: mediaSetting(state.media, buildMediaMapping(state, state.media), state.keptMedia),
    collectionId: defaults.collectionId ?? null,
    collectionLabel: defaults.collectionLabel ?? null,
    regLevelId: defaults.regLevelId ?? null,
    regLevelLabel: defaults.regLevelLabel ?? null,
    statusId: defaults.statusId ?? null,
    statusLabel: defaults.statusLabel ?? null,
    publish: state.publish,
    overwriteExisting: state.overwriteExisting,
    blankValues: blankValuesPayload(state.blankValues) ?? {},
    blankValuesRejected: blankValuesPayload(state.blankValuesRejected) ?? {},
    lookups: lookupsPayload(state.lookupDefinitions, state.columnNames) ?? [],
    valueReview: state.valueReview,
    profileId: state.profileId,
    profileDecisions: state.profileDecisions.length ? state.profileDecisions : undefined,
    carriedDecisions: state.carriedDecisions ?? undefined,
    view: state.view,
    profileBaseline: state.profileBaseline,
  };
}

/** The `/concept_values` body, less the loaded profile and the decisions
 * sent in its place: what a scan's result depends on, so the JSON of two
 * of them tells whether a scan is out of date. The synthetic targets are left out, and so are the group kinds
 * without the `group_id` wire key. */
export function conceptScanRequest(state: ConceptScanState): ConceptScanRequest {
  const mapping = buildMapping(state);
  const groups = groupPayloads(state.groups, mapping);
  return {
    ...sourceParams(state.source),
    object_type: state.objectType,
    mapping: withoutPseudoTargets(mapping),
    ...groupIdPayloads(kind => groups[groupIdWireKey(kind)]),
    blank_values: blankValuesPayload(state.blankValues),
    lookups: lookupsPayload(state.lookupDefinitions, state.columnNames),
  };
}

/**
 * The `/batch_fields` body: the mapping less the synthetic targets, the
 * `media_mapping`, and the `has_<kind>_group` flag of each kind that has
 * one and a card. `null` when no field is mapped, on the object or on the
 * linked media, and no card of any kind exists - nothing to ask for.
 */
export function batchFieldsRequest(state: BatchFieldsState): BatchFieldsRequest | null {
  const mapping = withoutPseudoTargets(buildMapping(state));
  const mediaMapping = buildMediaMapping(state, state.media);
  const inUse = (kind: GroupKind) => state.groups.models[kind].length > 0;
  if (mapping.length === 0 && mediaMapping.length === 0 && !state.groups.kinds.some(inUse)) {
    return null;
  }
  const flags: ImportModuleGroupFlags = {};
  for (const kind of state.groups.kinds) {
    const flag = groupFlagKey(kind);
    if (flag) {
      flags[flag] = inUse(kind) || undefined;
    }
  }
  return {object_type: state.objectType, mapping, media_mapping: nonEmpty(mediaMapping), ...flags};
}

/** The columns of each lookup's table that `mapping` reads through it, by
 * lookup key, each once, in mapping order. */
export function lookupLabelColumns(mapping: readonly Pick<MappingEntry, 'parts'>[]): Map<string, string[]> {
  const columns = new Map<string, string[]>();
  for (const part of mapping.flatMap(entry => entry.parts)) {
    if (part.kind === 'lookup' && part.label_column) {
      const taken = columns.get(part.value) ?? [];
      if (!taken.includes(part.label_column)) {
        columns.set(part.value, [...taken, part.label_column]);
      }
    }
  }
  return columns;
}

/** The `/lookup_check` body for every complete lookup, each with the
 * columns of its table the mapping reads through it (`labelColumns`, by
 * lookup key), or `null` while no lookup is complete or no table is
 * picked. A lookup's name changes no answer, so it is left out: renaming
 * one does not read the table again. */
export function lookupCheckRequest(
  source: SourceFile, lookups: readonly LookupDefinition[], columnNames: ReadonlySet<string>,
  blankValues: ImportModuleBlankValues, labelColumns: ReadonlyMap<string, readonly string[]>,
): LookupCheckRequest | null {
  const payload = lookupsPayload(lookups, columnNames);
  if (!payload || !source.table) {
    return null;
  }
  return {
    ...sourceParams(source),
    lookups: payload.map(({name, ...lookup}) => ({...lookup, label_columns: [...(labelColumns.get(lookup.key) ?? [])]})),
    include_codes: true,
    blank_values: blankValuesPayload(blankValues),
  };
}

// --- comparing profiles ---------------------------------------------------------

/** What tells two profile-save bodies apart: the configuration as JSON,
 * with its group, sub-block and lookup keys renamed by first appearance -
 * the page renumbers them on every apply - and each value decision by its
 * column and value. Plain JSON, so the session stash can hold it. */
export interface ProfileComparison {
  configuration: string;
  decisions: {[value: string]: DecisionContent};
}

/** A decision as a save stores it: the kind, the term picked (a pick names
 * it in `resolved_artifact_id`, or through its ticked candidate), and the
 * new term's name, code and parent. */
type DecisionContent = [
  ImportModuleDecisionRow['decision_kind'], string | null, string | null, string | null, string | null,
];

/** The profile lists whose entries carry a group key. */
const PROFILE_GROUP_LISTS: readonly GroupListKey[] = GROUP_LIST_KINDS.map(groupListKey);

/** The mapping-entry fields that name a group by its key. */
const ENTRY_GROUP_KEYS = ['group_id', 'history_event_group', 'admin_event_group'] as const;

export function profileComparison(body: ProfileSaveRequest): ProfileComparison {
  const {value_decisions: decisions, ...configuration} = body;
  return {
    configuration: JSON.stringify(keysByAppearance(configuration)),
    decisions: Object.fromEntries((decisions ?? []).map(row => [
      JSON.stringify([row.source_column, row.source_value_key]), decisionContent(row),
    ])),
  };
}

/** Whether `current` would save a different profile than `baseline`. A
 * value both hold counts when its decision differs, and a value only
 * `current` holds counts once it is decided. An undecided value `baseline`
 * lacks, and a value only `baseline` holds, do not count: a scan lists
 * values the profile never decided and can miss ones it did, and neither
 * is an edit. */
export function profileChanged(baseline: ProfileComparison, current: ProfileComparison): boolean {
  if (baseline.configuration !== current.configuration) {
    return true;
  }
  return Object.entries(current.decisions).some(([value, decision]) => {
    const saved = baseline.decisions[value];
    return saved === undefined ? decision[0] !== null : JSON.stringify(saved) !== JSON.stringify(decision);
  });
}

/** A decision row, possibly still carrying the candidate list it was
 * decided from. */
export type DecisionSource = ImportModuleDecisionRow & {candidates?: ImportModuleConceptCandidate[]};

/** `row` as a decision binds it, without its candidate list: a pick names
 * its term in `resolved_artifact_id`, taken from its ticked candidate when
 * it has none of its own. */
export function decidedRow(row: DecisionSource): ImportModuleDecisionRow {
  return {
    source_column: row.source_column,
    source_value: row.source_value,
    source_value_key: row.source_value_key,
    decision_kind: row.decision_kind,
    resolved_artifact_id: row.resolved_artifact_id
      ?? row.candidates?.find(candidate => candidate.is_selected)?.candidate_artifact_id ?? null,
    new_concept_name: row.new_concept_name,
    new_concept_code: row.new_concept_code,
    new_concept_parent_id: row.new_concept_parent_id ?? null,
  };
}

function decisionContent(row: DecisionSource): DecisionContent {
  const d = decidedRow(row);
  return [d.decision_kind, d.resolved_artifact_id, d.new_concept_name, d.new_concept_code, d.new_concept_parent_id];
}

/** Numbers keys by their first appearance, `<prefix>0`, `<prefix>1`, … */
class KeyNumbering {
  private readonly numbers = new Map<string, string>();

  constructor(private readonly prefix: string) {}

  of(key: string): string {
    if (!this.numbers.has(key)) {
      this.numbers.set(key, `${this.prefix}${this.numbers.size}`);
    }
    return this.numbers.get(key)!;
  }
}

/** `values` with its columns and each column's values in sorted order. */
function sortedBlankValues(values: ImportModuleBlankValues): ImportModuleBlankValues {
  return Object.fromEntries(Object.keys(values).sort().map(column => [column, [...values[column]].sort()]));
}

/** A copy of a profile-save body's configuration without the profile's
 * name or its null fields, with its keys renamed by first appearance: in
 * the mapping and the media join's mapping, then the group lists and the
 * lookups. A field left out
 * and one sent as null save the same profile, and a restore can turn one
 * into the other (a batch default's label, say). The blank and
 * rejected values are sorted by column and value: their order means
 * nothing, and an undo and a redo can change it. */
function keysByAppearance(configuration: Omit<ProfileSaveRequest, 'value_decisions'>): any {
  const copy = JSON.parse(JSON.stringify(configuration));
  delete copy.profile_name;
  for (const field of Object.keys(copy)) {
    if (copy[field] === null) {
      delete copy[field];
    }
  }
  const groups = new KeyNumbering('g');
  const subBlocks = new KeyNumbering('s');
  const lookups = new KeyNumbering('l');
  for (const entry of [...(copy.mapping ?? []), ...(copy.media?.mapping ?? [])]) {
    for (const field of ENTRY_GROUP_KEYS) {
      if (entry[field] !== undefined) {
        entry[field] = groups.of(entry[field]);
      }
    }
    if (entry.sub_block !== undefined) {
      entry.sub_block = subBlocks.of(entry.sub_block);
    }
    for (const part of entry.parts ?? []) {
      if (part.kind === 'lookup') {
        part.value = lookups.of(part.value);
      }
    }
  }
  for (const list of PROFILE_GROUP_LISTS) {
    for (const group of copy[list] ?? []) {
      group.key = groups.of(group.key);
      for (const sub of [...(group.named ?? []), ...(group.places ?? [])]) {
        sub.key = subBlocks.of(sub.key);
      }
    }
  }
  for (const lookup of copy.lookups ?? []) {
    lookup.key = lookups.of(lookup.key);
  }
  for (const field of ['blank_values', 'blank_values_rejected']) {
    if (copy[field]) {
      copy[field] = sortedBlankValues(copy[field]);
    }
  }
  return copy;
}
